import type { ReturnSchedulingRequestView } from '@/dashboard/api';
/**
 * AdoraCare - the "To schedule" BOOK door's pure derivations
 * (LAKSHMI-5-BOOK-DOOR-CORRECTIVE-V1; founder defect ruling 2026-08-16).
 *
 * The queue row carries the doctor's INTENT (interval token, note, treating
 * doctor); the Book door opens the EXISTING desk booking flow
 * (RegisterBookingOffer) prefilled from it -- propose-then-confirm, never
 * an auto-book. These helpers derive the two prefill facts:
 *
 *  - proposedFollowUpDate: interval token -> the PROPOSED clinic-day for
 *    the offer's date picker. HONESTY FENCES: the proposal derives ONLY
 *    when the token parses under the seeded key grammar
 *    (`review_2_weeks` / `one_week` / `3_days`-shaped tokens -- the
 *    DEFAULT_CASE_SHEET_FOLLOW_UP_OPTIONS vocabulary class); an
 *    unparseable org token, `no_follow_up`, or `custom` proposes NOTHING
 *    (the offer opens on its standing today default -- absence, never an
 *    invented date). The anchor is the visit's finalize evidence
 *    (closedAt) when present, else now; a proposal that lands in the past
 *    clamps to today (the desk cannot book the past).
 *  - scheduleBookHeadline: the intent restated at the booking moment, so
 *    the receptionist confirms WITH the doctor's words in view (no
 *    retyping, no memory).
 *
 * Date arithmetic rides THE ONE IST clinic-day convention (clinicToday,
 * day-scope.ts) -- never a second timezone read.
 */

import { clinicToday } from '@/dashboard/ui/day-scope';
import type { NextVisitScheduleRowView } from '@/walkin/WalkinFollowupsPanel';

const WORD_COUNTS: Readonly<Record<string, number>> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  twelve: 12,
};

/** The structured-interval grammar inside a follow-up option KEY: a count
 *  (digits or a number word) joined to a unit by underscores, anywhere in
 *  the token ("review_2_weeks", "one_week", "3_days"). */
const INTERVAL_RE =
  /(?:^|_)(\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|twelve)_(day|week|month)s?(?:_|$)/;

/** Parse the interval from an option key; null when the token carries no
 *  structured interval (no proposal derives -- honest absence). */
export function parseFollowUpInterval(
  optionKey: string,
): { count: number; unit: 'day' | 'week' | 'month' } | null {
  const m = INTERVAL_RE.exec(optionKey.trim().toLowerCase());
  if (m === null) return null;
  const raw = m[1] ?? '';
  const count = /^\d+$/.test(raw) ? Number(raw) : (WORD_COUNTS[raw] ?? 0);
  if (!Number.isSafeInteger(count) || count <= 0) return null;
  const unit = m[2] as 'day' | 'week' | 'month';
  return { count, unit };
}

/**
 * FRONT-DESK-DAY-BOARD-V1 (Partha TAB row "show recall due"): the recorded
 * intent's TARGET clinic-day (YYYY-MM-DD), UNCLAMPED -- the doctor's own
 * recorded follow-up option (a token from the org's configured case-sheet
 * vocabulary) applied to the visit's recorded close moment. This is the
 * recall-due DISPLAY fact: an overdue intent must READ as its true past
 * day, so no clamp lives here. Null when the token carries no structured
 * interval (`custom` / `no_follow_up` / an unparseable org token) or no
 * anchor resolves -- honest absence, never an invented cadence (founder
 * ruling 2026-08-21: follow-up dates are operator-chosen; this derives
 * NOTHING the doctor did not record). `nowMs` is injectable for tests and
 * anchors only the no-closedAt fallback.
 */
export function followUpTargetDate(
  optionKey: string,
  closedAtIso: string | null,
  nowMs: number = Date.now(),
  request?: Pick<ReturnSchedulingRequestView, 'targetDate'>,
): string | null {
  if (request !== undefined) return request.targetDate;
  const interval = parseFollowUpInterval(optionKey);
  if (interval === null) return null;
  const anchorMs =
    closedAtIso !== null && !Number.isNaN(Date.parse(closedAtIso))
      ? Date.parse(closedAtIso)
      : nowMs;
  const target = new Date(anchorMs);
  if (interval.unit === 'day') {
    target.setUTCDate(target.getUTCDate() + interval.count);
  } else if (interval.unit === 'week') {
    target.setUTCDate(target.getUTCDate() + interval.count * 7);
  } else {
    target.setUTCMonth(target.getUTCMonth() + interval.count);
  }
  return clinicToday(target);
}

/**
 * The PROPOSED clinic-day (YYYY-MM-DD) for the booking picker, or null when
 * no honest proposal exists. `nowMs` is injectable for tests. Rides THE ONE
 * target derivation above and adds ONLY the booking clamp -- byte-identical
 * output to its pre-extraction self.
 */
export function proposedFollowUpDate(
  optionKey: string,
  closedAtIso: string | null,
  nowMs: number = Date.now(),
  request?: Pick<ReturnSchedulingRequestView, 'targetDate'>,
): string | null {
  const proposed = followUpTargetDate(optionKey, closedAtIso, nowMs, request);
  if (proposed === null) return null;
  const today = clinicToday(new Date(nowMs));
  // The desk cannot book the past: an overdue intent proposes TODAY.
  return proposed < today ? today : proposed;
}

/** The intent line the booking offer restates as its headline. */
export function scheduleBookHeadline(row: {
  readonly optionKey: string;
  readonly optionLabel: string | null;
  readonly treatmentDoctorName: string | null;
}): string {
  return (
    'Next visit: ' +
    (row.optionLabel ?? row.optionKey) +
    (row.treatmentDoctorName ? ' - with ' + row.treatmentDoctorName : '')
  );
}

/**
 * TO-SCHEDULE-PATIENT-DISAMBIGUATION-V1: the row's SECOND IDENTIFIER, so two
 * same-name patients ("Jyothsna" x2) are told apart BEFORE the operator clicks
 * Book -- the named live defect in ADORACARE-VISUAL-CONTRACT-V1.md s.13.
 *
 * THE ONE label owner (the dayBoardUmrLabel discipline: one vocabulary, one
 * home). The identifier is the finalized visit's APPOINTMENT NUMBER -- the same
 * number the Home hero search accepts ("appointment no. e.g. 1042") and the
 * `Appointment #NNN` form NoShowRecoveryPage / IntakeDrawer already render, so
 * the operator reads (and can search) the same token everywhere. It is already
 * on the schedule wire (NextVisitScheduleRow.appointmentId) and is UNIQUE per
 * row, so the two rows are ALWAYS distinguishable -- never a widened wire, never
 * UMR (UMR is not on this desk wire; bringing it here is a founder-gated
 * wire-widening, not this package). PHI-free: an administrative visit id.
 */
export function scheduleRowIdLabel(appointmentId: number): string {
  return 'Appointment #' + appointmentId;
}

/** TRUE when the row carries a bookable identity key (patient or lead).
 *  A keyless row gets NO Book door (LES-016: no dead doors) -- the
 *  operator books it through search instead. */
export function scheduleRowBookable(row: {
  readonly patientId?: string | null;
  readonly leadId?: number | null;
}): boolean {
  return (
    (typeof row.patientId === 'string' && row.patientId !== '') ||
    (typeof row.leadId === 'number' && Number.isSafeInteger(row.leadId))
  );
}

/**
 * The RegisterBookingOffer identity props for a queue row: the PATIENT leg
 * leads when a golden id exists (the derivation's own key precedence);
 * the lead leg carries lead-only rows. Callers gate on scheduleRowBookable
 * first -- a keyless row maps to nothing bookable.
 */
export function scheduleRowBookingIdentity(row: {
  readonly appointmentId?: number;
  readonly caseSheetId?: number;
  readonly patientId?: string | null;
  readonly leadId?: number | null;
}): { patientId: string; leadId: number | null; careAppointmentId?: number; careCaseSheetId?: number } {
  if (typeof row.patientId === 'string' && row.patientId !== '') {
    return { patientId: row.patientId, leadId: null, ...(row.appointmentId&&row.caseSheetId?{careAppointmentId:row.appointmentId,careCaseSheetId:row.caseSheetId}:{}) };
  }
  return { patientId: '', leadId: row.leadId ?? null };
}

/** Re-exported for the panel's headline reuse (one derivation, two mounts). */
export type ScheduleBookRow = Pick<
  NextVisitScheduleRowView,
  'optionKey' | 'optionLabel' | 'treatmentDoctorName'
>;
